import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import {
  BlockBodyBase,
  BlockContainerBase,
  BlockHeader,
  BlockTitle,
} from "@/components/canvas-editor/components/Container";
import { CustomNodeFilterSelect } from "@/components/CustomNodeFilterSelect";
import { CustomNodeInstallStats } from "@/components/CustomNodeInstallStats";
import { CustomNodeSearch } from "@/components/CustomNodeSearch";
import { FavoriteCustomNodeIcon } from "@/components/FavoriteCustomNodeButton";
import { Page } from "@/components/Page";
import { Spinner } from "@/components/Spinner";
import { ButtonLink } from "@/components/ui/button";
import { H1, H2 } from "@/components/ui/typography";
import {
  getTopCustomNodes,
  getUserCustomNodesQueryOptions,
  searchCustomNodesQueryOptions,
} from "@/lib/api";
import {
  CustomNodeFilter,
  useDefaultCustomNodes,
} from "@/lib/useDefaultCustomNodes";

export const Route = createFileRoute("/app/_sidebar-layout/custom-nodes/")({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    // validate and parse the search params into a typed state
    return {
      tags: search.tags,
    } as { tags?: number[] };
  },
  loader: async ({ context: { queryClient } }) => {
    await Promise.all([
      queryClient.ensureInfiniteQueryData(
        searchCustomNodesQueryOptions({ query: "", favorited_only: true }),
      ),
      queryClient.ensureQueryData(getUserCustomNodesQueryOptions()),
      queryClient.ensureQueryData(getTopCustomNodes()),
    ]);
  },
  head: () => ({ meta: [{ title: "Create Custom Node" }] }),
});

function UserCustomNodesTable() {
  const { tags } = Route.useSearch();
  const [filter, setFilter] = useState<CustomNodeFilter>(
    tags?.length ? `${tags[0]}` : "all",
  );
  const { data = [], isFetching } = useDefaultCustomNodes({ filter });

  return (
    <div className="flex flex-col gap-4 mt-8">
      <H2 className="flex items-center gap-2">
        <span className="flex-1">Your Library</span>
        <CustomNodeFilterSelect filter={filter} setFilter={setFilter} />
      </H2>
      {data.length > 0 ? (
        <div className="flex flex-wrap gap-4">
          {data.map((node) => (
            <Link
              key={node.id}
              to="/app/custom-nodes/$nodeId/view"
              params={{ nodeId: node.id.toString() }}
            >
              <BlockContainerBase
                asDiv
                variant={node.component.metadata?.color}
              >
                <BlockHeader
                  description={
                    node.description || "A custom node created by you!"
                  }
                >
                  <FavoriteCustomNodeIcon node={node} />
                  <BlockTitle title="Custom Node" />
                </BlockHeader>

                <BlockBodyBase>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-4">
                      <div>{node.title}</div>
                      {node.public && (
                        <div className="text-sm bg-slatea-5 dark:bg-slatedarka-5 rounded-lg px-2 py-1">
                          Public
                        </div>
                      )}
                    </div>
                  </div>
                </BlockBodyBase>
              </BlockContainerBase>
            </Link>
          ))}
        </div>
      ) : isFetching ? (
        <div className="text-slate-dim">
          <Spinner />
        </div>
      ) : (
        <div className="text-slate-dim">
          {filter === "favorited"
            ? "You haven't favorited any custom nodes yet."
            : filter === "my_nodes"
            ? "You haven't created any custom nodes yet."
            : filter === "all"
            ? "You haven't created any custom nodes or favorited any yet."
            : "No matching custom nodes found for tag."}
        </div>
      )}
    </div>
  );
}

function TopCustomNodesTable() {
  const { data } = useSuspenseQuery(getTopCustomNodes());

  if (!data || !data.length) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-center gap-4">
      {data.map((node) => (
        <Link
          key={node.id}
          to="/app/custom-nodes/$nodeId/view"
          params={{ nodeId: node.id.toString() }}
        >
          <BlockContainerBase asDiv variant={node.component.metadata?.color}>
            <BlockHeader
              description={node.description || "A custom node created by you!"}
            >
              <FavoriteCustomNodeIcon node={node} />
              <BlockTitle title="Custom Node" />
            </BlockHeader>

            <BlockBodyBase>
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-4">
                  <div>{node.title}</div>
                </div>
                <CustomNodeInstallStats {...node} />
              </div>
            </BlockBodyBase>
          </BlockContainerBase>
        </Link>
      ))}

      <ButtonLink variant="ghost" to="/app/custom-nodes/search">
        View More
      </ButtonLink>
    </div>
  );
}

function RouteComponent() {
  const navigate = useNavigate();

  const onSelect = (id: number) =>
    navigate({
      to: "/app/custom-nodes/$nodeId/view",
      params: { nodeId: id.toString() },
    });

  const onSearch = (query: string) =>
    navigate({
      to: "/app/custom-nodes/search",
      search: { q: query },
    });

  return (
    <Page>
      <div className="flex items-center justify-between">
        <H1>Custom Nodes</H1>
        <ButtonLink to="/app/custom-nodes/create">Create</ButtonLink>
      </div>

      <CustomNodeSearch onSelect={onSelect} onSearch={onSearch} />
      <TopCustomNodesTable />
      <UserCustomNodesTable />
    </Page>
  );
}
